/**
 * App.tsx — Sovereign Memorial Factory
 * Multi-tenant domain-aware router.
 *
 * Tenant detection is performed once at runtime by reading
 * window.location.hostname. The result drives which route
 * tree is mounted — no server-side logic required.
 *
 * D2C  →  goldenchronicle.org  (and localhost / unknown)
 *   /          → LandingPage    (hero / marketing stub)
 *   /intake    → Home           (memorial intake form)
 *   /director  → Director       (director dashboard — admin access on D2C)
 *
 * B2B  →  cherishedlegacy.org
 *   /          → SalesPage      (partner / funeral home stub)
 *   /director  → Director       (director dashboard)
 */
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import LandingPage from "./LandingPage";
import SalesPage   from "./SalesPage";
import Home        from "./Home";
import Director    from "./src/pages/Director";

// ── Tenant detection ──────────────────────────────────────────────────────────
function getTenant(): "d2c" | "b2b" {
  if (typeof window === "undefined") return "d2c";
  const host = window.location.hostname;
  if (host.includes("cherishedlegacy")) return "b2b";
  return "d2c"; // goldenchronicle, localhost, or any unknown host
}

// ── 404 ───────────────────────────────────────────────────────────────────────
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="text-center">
        <h1 className="text-4xl font-serif font-semibold text-stone-800 mb-2">404</h1>
        <p className="text-stone-500">Page not found.</p>
      </div>
    </div>
  );
}

// ── Route trees ───────────────────────────────────────────────────────────────
function D2CRoutes() {
  return (
    <Routes>
      <Route path="/"         element={<LandingPage />} />
      <Route path="/intake"   element={<Home />} />
      <Route path="/director" element={<Director />} />
      {/* Legacy: /b2b redirect to root */}
      <Route path="/b2b"      element={<Navigate to="/" replace />} />
      <Route path="*"         element={<NotFound />} />
    </Routes>
  );
}

function B2BRoutes() {
  return (
    <Routes>
      <Route path="/"         element={<SalesPage />} />
      <Route path="/director" element={<Director />} />
      <Route path="*"         element={<NotFound />} />
    </Routes>
  );
}

// ── App root ──────────────────────────────────────────────────────────────────
export default function App() {
  const tenant = getTenant();

  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors />
      {tenant === "b2b" ? <B2BRoutes /> : <D2CRoutes />}
    </BrowserRouter>
  );
}
